import { useEffect, useRef, useState } from 'react';

import { fetchAuthedBlobUrl, revokeAuthedBlobUrl } from '@/utilities/mediaAudio';

export type AuthedImageState = 'idle' | 'loading' | 'ok' | 'failed';

/** Local previews (a just-picked File) never touch the network. */
const isLocalUrl = (url: string) =>
  url.startsWith('blob:') || url.startsWith('data:');

interface Resolution {
  url: string | null;
  objectUrl: string | null;
  state: AuthedImageState;
}

export function useAuthedImageUrl(url?: string | null): {
  src: string | null;
  state: AuthedImageState;
} {
  const [resolved, setResolved] = useState<Resolution>({
    url: null,
    objectUrl: null,
    state: 'idle',
  });
  const currentUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    revokeAuthedBlobUrl(currentUrlRef.current);
    currentUrlRef.current = null;

    if (!url) {
      setResolved({ url: null, objectUrl: null, state: 'idle' });
      return undefined;
    }

    if (isLocalUrl(url)) {
      setResolved({ url, objectUrl: null, state: 'ok' });
      return undefined;
    }

    setResolved({ url, objectUrl: null, state: 'loading' });

    fetchAuthedBlobUrl(url)
      .then(({ objectUrl }) => {
        if (cancelled) {
          revokeAuthedBlobUrl(objectUrl);
          return;
        }
        currentUrlRef.current = objectUrl;
        setResolved({ url, objectUrl, state: 'ok' });
      })
      .catch(() => {
        if (!cancelled) setResolved({ url, objectUrl: null, state: 'failed' });
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(
    () => () => {
      revokeAuthedBlobUrl(currentUrlRef.current);
      currentUrlRef.current = null;
    },
    [],
  );

  if (!url) return { src: null, state: 'idle' };
  if (isLocalUrl(url)) return { src: url, state: 'ok' };

  // The effect has not run for this url yet — say nothing rather than hand
  // back the previous url's verdict.
  if (resolved.url !== url) return { src: null, state: 'loading' };

  // `loading` deliberately yields no src — see the header comment.
  return {
    src: resolved.objectUrl || (resolved.state === 'failed' ? url : null),
    state: resolved.state,
  };
}
